import type { Form, FormListStats, FormType } from '@jfh/intake-shared';

/** The Forms list's status and change wording (F10 P4). */

export interface FormStatus {
  label: string;
  tone: 'approved' | 'neutral';
  /** "Has changes not yet published" under the badge. */
  note: string | null;
}

export function formStatus(form: Form, stats: FormListStats | undefined): FormStatus {
  if (form.archivedAt) return { label: 'Archived', tone: 'neutral', note: null };
  const live = form.status === 'published' && form.currentVersionId !== null;
  if (!live) return { label: 'Not published yet', tone: 'neutral', note: null };
  const label = form.settings.source === 'wix' ? 'Live on Wix' : `Live · version ${stats?.version ?? '?'}`;
  return { label, tone: 'approved', note: stats?.unpublishedChanges ? 'Has changes not yet published' : null };
}

const DAY_MONTH = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short' });
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric' });
const LONDON_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' });

/** "Hayden Hutchins, yesterday" / "Grace Okafor, 8 Oct" / "3 Feb 2025". */
export function changedLine(stats: FormListStats | undefined, now = new Date()): string {
  if (!stats) return '';
  const at = new Date(stats.changedAt);
  if (Number.isNaN(at.getTime())) return stats.changedBy ?? '';
  const day = (d: Date) => LONDON_DAY.format(d);
  const yesterday = new Date(now.getTime() - 86_400_000);
  const when =
    day(at) === day(now)
      ? 'today'
      : day(at) === day(yesterday)
        ? 'yesterday'
        : day(at).slice(0, 4) === day(now).slice(0, 4)
          ? DAY_MONTH.format(at)
          : DAY_MONTH_YEAR.format(at);
  return stats.changedBy ? `${stats.changedBy}, ${when}` : when.charAt(0).toUpperCase() + when.slice(1);
}

/** The New form dialog's choices, each saying who approves. */
export const NEW_FORM_TYPES: { value: FormType; label: string; description: string }[] = [
  { value: 'trade_account', label: 'Trade account', description: 'Customer Service approves. Sets the account up in iQ.' },
  { value: 'credit_account', label: 'Credit account', description: 'Customer Service, then Accounts approve the credit terms.' },
  { value: 'supplier_account', label: 'Supplier account', description: 'The supplier team approves. Sets the supplier up in iQ.' },
  { value: 'other', label: 'Something else', description: 'No approval. Answers can go to iQ as a lead.' },
];
